import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { NavigationIconLinkType } from '@allesauto/utils/consts';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import {
  Catalog16,
  Chat16,
  Favorite16,
  Information16,
  Logout16,
  Restart16,
  StayInside16,
  Task16,
  User16,
  UserFollow16,
  UserProfile16,
} from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, useMemo } from 'react';

import NextLink from '../nextLink';

export interface NavigationIconLinkProps {
  icon: NavigationIconLinkType;
  name: string;
  href: string;
  isLastGeneral?: boolean;
  isLastAccount?: boolean;
  hasNotification?: boolean;
  isHeaderNavigation?: boolean;
  isHeaderUserMenu?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
  onClickHandler?: () => void;
}

const mobileLinkIcons = {
  contact: <Chat16 />,
  helpFaq: <Information16 />,
  user: <User16 />,
  userRegister: <UserFollow16 />,
  stayInside: <StayInside16 />,
  task: <Task16 />,
  userProfile: <UserProfile16 />,
  catalog: <Catalog16 />,
  logout: <Logout16 />,
  favorite: <Favorite16 />,
};

const NavigationIconLink: FC<NavigationIconLinkProps> = (props) => {
  const {
    icon,
    href,
    name,
    isLastGeneral = false,
    isLastAccount = false,
    hasNotification = false,
    isHeaderNavigation = true,
    isHeaderUserMenu = false,
    isActive = false,
    isLoading = false,
    onClickHandler,
  } = props;

  const whiteLabel = useWhiteLabel();

  const className = useMemo(
    () => ({
      containerClass: clsx(
        'flex items-center',
        isHeaderNavigation
          ? 'pb-30px'
          : 'py-20px md:px-[53px] lg:px-60px xl:px-80px',
        isHeaderUserMenu && 'h-[50px] !px-0 py-15px',
        isLastGeneral && 'pb-25px',
        isLastAccount && 'pb-35px',
        isHeaderUserMenu
          ? 'text-purple90 monochrome:!text-black'
          : isActive
          ? `${
              isAuregoShop(whiteLabel)
                ? 'wl-user-details wl-button-color'
                : 'wl-text-highlight wl-custom-text-highlight wl-menu-item-text-color'
            }`
          : `monochrome:text-white ${whiteLabel && 'wl-user-details'}`
      ),
      linkClass: clsx(
        'flex items-center no-underline',
        isActive
          ? `text-green60 wl-text-brand wl-menu-item-text-color${
              isAuregoShop(whiteLabel) &&
              'wl-user-details wl-button-color wl-user-details-right-border'
            }`
          : 'text-white'
      ),
      textLinkClass: clsx(
        'font-regular font-weight-normal icon-color-aurego pl-10px',
        isHeaderUserMenu ? 'text-p2' : 'text-p1',
        isActive
          ? isAuregoShop(whiteLabel) && 'wl-user-details wl-button-color'
          : `monochrome:text-white ${
              isAuregoShop(whiteLabel) && '!text-black wl-user-details'
            }`
      ),
      notificationClass: 'bg-red60 h-10px rounded-circle w-10px ml-10px',
      loadingIcon: clsx('animate-spin'),
    }),
    [
      isActive,
      isHeaderNavigation,
      isHeaderUserMenu,
      isLastAccount,
      isLastGeneral,
      whiteLabel,
    ]
  );

  return (
    <NextLink
      href={href}
      onClick={onClickHandler}
      className={className.linkClass}
      data-wl-menu-option-set-color
    >
      <div className={className.containerClass}>
        {isLoading ? (
          <Restart16 className={className.loadingIcon} />
        ) : (
          icon && mobileLinkIcons[icon]
        )}
        <span className={className.textLinkClass}>{name}</span>
        {hasNotification && (
          <span className={className.notificationClass}></span>
        )}
      </div>
    </NextLink>
  );
};

export default NavigationIconLink;
